import React from "react";

export const ComparativeAnalysisPanel = React.forwardRef(
  function ComparativeAnalysisPanel(_props, ref) {
    return (
      <div ref={ref} className="nl-panel-body nl-response-html">
      <h3>1. Areas of Agreement</h3>
      <ul>
        <li>
          All four models identify substantial performance, timely cure, and buyer
          acceptance conduct as the strongest defense themes.
        </li>
        <li>
          All models agree that documentation of cure timing and post-cure conduct
          is critical.
        </li>
        <li>
          All models flag time-of-essence language as the primary vulnerability.
        </li>
      </ul>

      <h3>2. Areas of Disagreement</h3>
      <ul>
        <li>ChatGPT emphasizes substantial performance and buyer acceptance.</li>
        <li>Claude emphasizes cure tender, mitigation, and damages control.</li>
        <li>Gemini centers governing law, venue, and strict timing risk.</li>
        <li>
          Perplexity focuses on authority verification and jurisdiction-specific
          research.
        </li>
      </ul>

      <h3>3. Reasoning Quality Differences</h3>
      <ul>
        <li>ChatGPT provides the clearest merits framing.</li>
        <li>Claude gives the strongest practical litigation posture.</li>
        <li>Gemini identifies the most important procedural risk.</li>
        <li>
          Perplexity is useful for authority direction, but citations and
          controlling authority must be verified before use.
        </li>
      </ul>

      <h3>4. Missing Facts Identified</h3>
      <ul>
        <li>Final forum-selection and governing-law provisions.</li>
        <li>Full cure-tender email chain.</li>
        <li>Buyer acceptance, refusal, or objection communications.</li>
        <li>Plaintiff&apos;s mitigation record.</li>
        <li>
          Contract characterization if goods/services classification matters.
        </li>
      </ul>

      <h3>5. Critical Vulnerabilities (Ranked)</h3>
      <ul>
        <li>
          <strong>1. Time-of-Essence Clause Strictness — CRITICAL</strong>
          <br />
          Risk: Express timing language may override substantial performance.
          Consequence: Defense weakens if timing is treated as a material
          condition. Action: Confirm governing law and brief the favorable
          standard early.
        </li>
        <li>
          <strong>2. Cure Documentation — HIGH</strong>
          <br />
          Risk: Cure tender may be legally insufficient without a clear written
          record. Consequence: Fact dispute may prevent early resolution. Action:
          Collect the complete cure-tender timeline and communications.
        </li>
        <li>
          <strong>3. Governing Law / Venue — HIGH</strong>
          <br />
          Risk: A strict jurisdiction may treat late delivery more harshly.
          Consequence: Strategy changes depending on forum. Action: Lock venue
          and governing-law theory before dispositive motion practice.
        </li>
        <li>
          <strong>4. Mitigation Evidence — MODERATE</strong>
          <br />
          Risk: Plaintiff may preserve damages despite cure. Consequence:
          Liability defense may not eliminate exposure. Action: Build a
          mitigation record and challenge damages causation.
        </li>
      </ul>

      <h3>6. Additional Facts Needed</h3>
      <ul>
        <li>
          <strong>Cure Evidence:</strong> Full email chain after cure tender;
          documented acceptance, refusal, or objection grounds.
        </li>
        <li>
          <strong>Contract Terms:</strong> Cure window, timing clause,
          acceptance language, and remedies provision.
        </li>
        <li>
          <strong>Authority:</strong> District-specific precedent on cure tender
          and time-of-essence enforcement.
        </li>
        <li>
          <strong>Damages:</strong> Plaintiff&apos;s mitigation log and evidence
          of actual prejudice.
        </li>
      </ul>

      <h3>7. Material Disagreements with Recommended Position</h3>
      <ul>
        <li>
          ChatGPT recommends leading with substantial performance and buyer
          acceptance.
        </li>
        <li>
          Claude recommends emphasizing cure tender, mitigation, and damages
          control.
        </li>
        <li>
          Gemini recommends resolving governing law and venue before dispositive
          motion practice.
        </li>
        <li>
          Perplexity recommends verifying controlling authority before relying on
          trend-level research.
        </li>
      </ul>
      <p>
        <strong>Recommended Position:</strong> Lead with substantial performance
        and timely cure. Preserve cure, waiver, and estoppel theories in the
        alternative. Confirm governing law early so opposing counsel does not
        control the legal framework.
      </p>

      <h3>8. Strategic Assessment</h3>
      <p>
        Overall position: <strong>STRONG</strong>, but fact-dependent.
        Cross-model consensus supports the defensive theory if the record
        confirms timely cure, buyer acceptance conduct, and lack of prejudice.
      </p>
      <p>
        The main risk is strict enforcement of time-of-essence language, which
        should be addressed early through governing-law and venue strategy.
      </p>
    </div>
  );
  },
);

export const FinalSynthesisPanel = React.forwardRef(
  function FinalSynthesisPanel(_props, ref) {
    return (
      <div ref={ref} className="nl-panel-body nl-response-html">
      <h3>1. Issue(s) Presented</h3>
      <ul>
        <li>
          Whether late delivery constitutes material breach where partial cure was
          tendered within the contractual cure window.
        </li>
        <li>
          Whether buyer acceptance, cure conduct, and lack of prejudice reduce or
          defeat materiality.
        </li>
      </ul>

      <h3>2. Relevant Rules / Legal Principles</h3>
      <ul>
        <li>
          Substantial performance focuses on whether the non-breaching party
          received the essential benefit of the bargain.
        </li>
        <li>
          Cure doctrine may reduce materiality where performance was corrected
          within an agreed cure window.
        </li>
        <li>
          Time-of-essence language may control if governing law treats strict
          timing as a material condition.
        </li>
        <li>
          Mitigation principles may limit damages even where a breach occurred.
        </li>
      </ul>

      <h3>3. Application / Analysis</h3>
      <p>
        The strongest defense is that timely cure preserved the essential benefit
        of the bargain. ChatGPT and Claude support that merits theory. Gemini
        properly warns that governing law may control the time-of-essence issue.
        Perplexity highlights the need to verify controlling authority before
        relying on broader case trends.
      </p>
      <p>
        The defense should combine merits, procedure, and evidence: lead with
        substantial performance and timely cure, confirm governing law, and
        build the factual record around cure timing, buyer acceptance, and lack
        of prejudice.
      </p>

      <h3>4. Vulnerability Analysis</h3>
      <ul>
        <li>
          <strong>PRIMARY VULNERABILITY (CRITICAL): Time-of-Essence Clause</strong>
          <br />
          Risk: Express timing language may defeat substantial performance
          arguments. Consequence: The defense weakens substantially in a strict
          timing jurisdiction. Action: Confirm governing law and brief the
          favorable standard early.
        </li>
        <li>
          <strong>SECONDARY VULNERABILITY: Cure Documentation</strong>
          <br />
          Risk: The record may not clearly establish timely and commercially
          reasonable cure. Consequence: The court may find a factual dispute.
          Action: Collect the complete written cure timeline.
        </li>
        <li>
          <strong>SECONDARY VULNERABILITY: Mitigation / Damages</strong>
          <br />
          Risk: Plaintiff may argue damages continued despite cure.
          Consequence: Damages exposure may remain even if liability is narrowed.
          Action: Build a mitigation record and challenge causation.
        </li>
      </ul>

      <h3>5. Strategic Posture Assessment</h3>
      <p>
        Position: <strong>STRONG</strong>, but fact-dependent. Cross-model
        consensus supports the defensive theory, but the outcome depends on
        governing law, cure documentation, acceptance conduct, and prejudice.
      </p>

      <h3>6. Corrective Actions</h3>
      <ul>
        <li>Confirm forum-selection and governing-law provisions.</li>
        <li>Build a complete cure-tender timeline.</li>
        <li>Preserve substantial performance, cure, waiver, and estoppel theories.</li>
        <li>Develop evidence showing buyer acceptance or lack of prejudice.</li>
        <li>Document mitigation issues to reduce damages exposure.</li>
      </ul>

      <h3>7. Conclusion</h3>
      <p>
        Lead with substantial performance and timely cure. Preserve alternative
        cure and waiver theories. Confirm governing law early. Build the
        evidentiary record before opposing counsel attacks the same gaps.
      </p>

      <h3>8. Additional Facts Needed</h3>
      <ul>
        <li>Final forum-selection and governing-law provisions.</li>
        <li>Cure-tender email chain.</li>
        <li>Buyer response, acceptance, or refusal communications.</li>
        <li>Plaintiff&apos;s mitigation log.</li>
        <li>
          District-specific authority on cure and time-of-essence enforcement.
        </li>
      </ul>
    </div>
  );
  },
);
